/**
 * Utility functions for text formatting
 */

/**
 * Enhances markdown formatting for better readability
 * @param {string} text - The text to format
 * @returns {string} - The formatted text
 */
export const enhanceMarkdownFormatting = (text) => {
  if (!text) return text;
  
  // Ensure headers have proper spacing
  let formatted = text.replace(/^(#{1,6})([^ ])/gm, '$1 $2');
  
  // Ensure list items have proper spacing
  formatted = formatted.replace(/^([*-])([^ ])/gm, '$1 $2');
  
  // Add blank lines before headers for better readability
  formatted = formatted.replace(/^(#{1,6} .*)/gm, '\n$1');
  
  // Clean up multiple blank lines
  formatted = formatted.replace(/\n{3,}/g, '\n\n');
  
  return formatted.trim();
};

/**
 * Converts text to bullet points
 * @param {string} text - The text to convert
 * @returns {string} - The text with bullet points
 */
export const convertToBulletPoints = (text) => {
  if (!text) return text;
  
  // Split by paragraphs and convert each to a bullet point
  const paragraphs = text.split(/\n{2,}/);
  return paragraphs
    .filter(p => p.trim().length > 0)
    .map(p => `• ${p.trim().replace(/\n/g, ' ')}`)
    .join('\n\n');
};

/**
 * Cleans up text by normalizing line breaks and removing extra whitespace
 * @param {string} text - The text to clean
 * @returns {string} - The cleaned text
 */
export const cleanupText = (text) => {
  if (!text) return text;
  
  // Normalize line breaks
  let cleaned = text.replace(/\r\n/g, '\n');
  
  // Remove extra whitespace
  cleaned = cleaned.replace(/ +/g, ' ');
  
  // Clean up multiple blank lines
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
  
  return cleaned.trim();
};

/**
 * Formats JSON text if valid
 * @param {string} text - The text to format as JSON
 * @returns {string} - The formatted JSON or original text if invalid
 */
export const formatJSON = (text) => {
  if (!text) return text;
  
  try {
    const jsonObj = JSON.parse(text);
    return JSON.stringify(jsonObj, null, 2);
  } catch (e) {
    console.warn('Could not parse as JSON, keeping original text');
    return text;
  }
};

/**
 * Formats a question and answer options according to AWS certification style
 * @param {string} text - The text to format
 * @returns {string} - The formatted question and answers
 */
export const formatQuestion = (text) => {
  if (!text) return text;
  
  // First, clean up excessive whitespace and line breaks
  let cleanedText = text
    .replace(/\r\n/g, '\n')  // Normalize line endings
    .replace(/\n{3,}/g, '\n\n')  // Replace 3+ consecutive newlines with 2
    .replace(/[ \t]+/g, ' ')  // Replace multiple spaces/tabs with single space
    .trim();
  
  // Find the position of the first question mark
  const questionMarkIndex = cleanedText.indexOf("?");
  
  if (questionMarkIndex === -1) {
    console.warn("No question mark (?) found in the text.");
    return cleanedText;
  }
  
  // Get text after the question mark
  const textAfterQuestionMark = cleanedText.substring(questionMarkIndex + 1).trim();
  
  // Check for selection instructions like "(Select two.)" or "(Select TWO.)" and similar patterns
  const selectionMatch = /^\s*(\([Ss]elect\s+((?:one|two|three|four|[0-9]+)|(?:ONE|TWO|THREE|FOUR))\.?\))/i;
  const selectionInstructionMatch = textAfterQuestionMark.match(selectionMatch);
  
  let question, answersPart;
  
  if (selectionInstructionMatch) {
    // Include the selection instruction as part of the question
    const instructionLength = selectionInstructionMatch[0].length;
    question = cleanedText.substring(0, questionMarkIndex + 1) + " " + selectionInstructionMatch[0];
    answersPart = textAfterQuestionMark.substring(instructionLength).trim();
  } else {
    // Regular extraction - everything before the ? is the question
    question = cleanedText.substring(0, questionMarkIndex + 1).trim();
    answersPart = textAfterQuestionMark;
  }
  
  // Clean up the question text - convert multiple line breaks to single spaces within paragraphs
  question = question
    .replace(/\n+/g, ' ')  // Replace line breaks with spaces
    .replace(/\s+/g, ' ')  // Replace multiple spaces with single space
    .trim();
  
  // If there are no answer options, just return the cleaned question
  if (!answersPart || answersPart.length === 0) {
    return question;
  }
  
  // Smart parsing of answer options
  let answerOptions = [];
  
  // First, try to split by line breaks if they exist
  if (/[\n\r]/.test(answersPart)) {
    answerOptions = answersPart
      .split(/[\n\r]+/)
      .map((option) => option.trim())
      .filter((option) => option.length > 0);
  } else {
    // If no line breaks, we need to intelligently parse the text
    // Look for patterns that indicate separate answer options
    
    // Method 1: Try to identify answer options by looking for common AWS service patterns
    // This handles cases where answers are separated by tabs or multiple spaces
    const tabOrMultiSpaceSplit = answersPart.split(/\t+|\s{2,}/);
    if (tabOrMultiSpaceSplit.length > 1) {
      answerOptions = tabOrMultiSpaceSplit
        .map((option) => option.trim())
        .filter((option) => option.length > 0);
    } else {
      // Method 2: Look for AWS service name patterns to split intelligently
      // Common AWS service patterns: Amazon/AWS followed by service name
      const awsServicePattern = /\b(?:Amazon|AWS)\s+[A-Z][a-zA-Z0-9\s-]+/g;
      const matches = answersPart.match(awsServicePattern);
      
      if (matches && matches.length >= 2) {
        // Use the AWS service matches as answer options
        answerOptions = matches.map(match => match.trim());
      } else {
        // Method 3: Split by capital letters that start new sentences/phrases
        // This catches patterns like "ServiceName ServiceName2 ServiceName3"
        const capitalLetterSplit = answersPart.split(/(?=[A-Z][a-z])/);
        if (capitalLetterSplit.length > 1) {
          answerOptions = capitalLetterSplit
            .map((option) => option.trim())
            .filter((option) => option.length > 0);
        } else {
          // Method 4: Last resort - split by spaces but try to group intelligently
          // Look for common word boundaries that might indicate separate options
          const words = answersPart.split(/\s+/);
          const groupedOptions = [];
          let currentOption = [];
          
          for (let i = 0; i < words.length; i++) {
            currentOption.push(words[i]);
            
            // Check if this might be the end of an option
            // Look for words that typically end AWS service descriptions
            if (words[i].match(/performance|solution|service|database|storage|compute|network|security|management$/i) ||
                (i < words.length - 1 && words[i + 1].match(/^(Amazon|AWS|Using|Increasing|Training|Creating)/i))) {
              groupedOptions.push(currentOption.join(' '));
              currentOption = [];
            }
          }
          
          // Add any remaining words as the last option
          if (currentOption.length > 0) {
            groupedOptions.push(currentOption.join(' '));
          }
          
          answerOptions = groupedOptions.filter(option => option.trim().length > 0);
        }
      }
    }
  }
  
  // If we still don't have good options, return just the question
  if (answerOptions.length === 0) {
    return question;
  }
  
  // Format the output with the question and lettered answer options
  const letters = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O", "P"];
  
  // Format the output
  let formattedOutput = `${question}\n\n`;
  
  // Add answers
  answerOptions.forEach((option, index) => {
    if (index < letters.length) {
      const letter = letters[index];
      formattedOutput += `${letter}) ${option}\n`;
    }
  });
  
  return formattedOutput.trim();
};

/**
 * Formats text as a comment revision with line numbers and revision markers
 * @param {string} text - The text to format
 * @returns {string} - The formatted text with comment revision markers
 */
export const formatCommentRevision = (text) => {
  // Simply return the text without any additional formatting
  return text;
};

/**
 * Applies the specified formatting to the text
 * @param {string} text - The text to format
 * @param {string} formatType - The type of formatting to apply
 * @returns {string} - The formatted text
 */
export const applyFormatting = (text, formatType) => {
  if (!text) return text;
  
  switch (formatType) {
    case 'markdown':
      return enhanceMarkdownFormatting(text);
    case 'json':
      return formatJSON(text);
    case 'bullets':
      return convertToBulletPoints(text);
    case 'clean':
      return cleanupText(text);
    case 'question':
      return formatQuestion(text);
    case 'comment-revision':
      return formatCommentRevision(text);
    default:
      return text;
  }
};
